import { produce } from 'immer';

import { Fee, isProcessedPayoff, PaidOffRecord, Payment, Payoff, PayoffRecord } from '@willow/types-iso';

import { add } from './math';

export const getUnpaidEscrow = (escrowBalance: number): number =>
  // A negative escrow balance indicates the borrower owes that amount to the lender (so multiple by * -1 to indicate an amount owed)
  // Otherwise, there is $0 of unpaid escrow
  escrowBalance < 0 ? escrowBalance * -1 : 0;

export const getPayoffFeesAmount = (fee?: Fee): number => fee?.amountRemaining || 0;

export const getProcessedPayoffFee = (payoffFee: Fee | undefined, paymentsMade: Payment[]): Fee | undefined => {
  if (!payoffFee) {
    return;
  }

  // See if any pending payments have processed, and if so, reverse them off
  return produce(payoffFee, (draft) => {
    draft.status = 'PAID';
    draft.lineItems.forEach((item, i) => {
      if (item.type === 'PENDING_PAYMENT_AT_PAYOFF' && item.paymentId) {
        const payment = paymentsMade.find((p) => p.id === item.paymentId);
        if (!payment) {
          throw new Error('Could not find payment associated with PENDING_PAYMENT_AT_PAYOFF fee');
        }
        // If the payment is no longer pending, don't expect this fee
        if (payment.status !== 'PENDING') {
          draft.lineItems[i].amount = 0;
        }
      }
    });

    const newFeeTotal = add(...draft.lineItems.map((item) => item.amount));
    draft.totalAmount = newFeeTotal;
    draft.amountRemaining = newFeeTotal;
  });
};

export const getPayoffTotal = ({
  outstandingBalance,
  outstandingInterest,
  outstandingCharges,
  payoffFeeAmount,
  unpaidEscrow, // payoff only considers money owed from escrow (ie negative escrow balance)
  suspenseBalance,
  reserveBalance,
  defermentWorkoutAmount, // FOLLOWUP i10204
  deferredPrincipalBalance,
}: {
  outstandingBalance: number;
  outstandingInterest: number;
  outstandingCharges: number;
  payoffFeeAmount: number;
  unpaidEscrow: number;
  suspenseBalance: number;
  reserveBalance: number;
  defermentWorkoutAmount: number;
  deferredPrincipalBalance: number;
}): number => {
  const totalDue = add(
    outstandingBalance,
    outstandingInterest,
    outstandingCharges,
    payoffFeeAmount,
    unpaidEscrow,
    suspenseBalance * -1, // Cash in suspense is sent back to the borrower, so it's displayed as negative if positive and vice versa;
    reserveBalance * -1, // same ^^
    defermentWorkoutAmount,
    deferredPrincipalBalance,
  );
  return totalDue;
};

export const getPayoffRecord = (paidOffRecord: PaidOffRecord, payoffs: Payoff[]): PayoffRecord | undefined => {
  const payoff = payoffs.find((item) => item.id === paidOffRecord.id);
  return payoff && isProcessedPayoff(payoff) ? payoff.processedRecord : undefined;
};
